import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api, refreshAccessToken } from "@/lib/api/client";
import { API_ORIGIN } from "@/lib/config";
import { getAccessToken } from "@/lib/auth/tokens";
import i18n from "@/lib/i18n/i18n";
import type { components } from "@/lib/api/schema.gen";
import { unwrap } from "@/lib/api/unwrap";

import type {
  ActionToCommunicate,
  CommunicationProfile,
  CommunicationWebPage,
  CustomActionToCommunicate,
  IssueChoice,
  PatchedCommunicationProfile,
} from "./types";

/**
 * Query-key factory for the communications slice. Each list exposes a stable
 * root so a mutation can invalidate the whole subtree after a write.
 */
export const commKeys = {
  all: ["communications"] as const,
  profile: () => [...commKeys.all, "profile"] as const,
  issues: () => [...commKeys.all, "issues"] as const,
  actions: () => [...commKeys.all, "actions"] as const,
  customActions: () => [...commKeys.all, "custom-actions"] as const,
  preview: () => [...commKeys.all, "preview"] as const,
  review: (id: string) => [...commKeys.all, "review", id] as const,
  pendingReviews: () => [...commKeys.all, "pending-reviews"] as const,
};

// --- profile -----------------------------------------------------------------

export function useProfileQuery(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: commKeys.profile(),
    enabled: options?.enabled,
    queryFn: () => unwrap(api.GET("/api/v1/communications/profile/")),
  });
}

/**
 * Persist a (partial) profile. Honours the AbortSignal so useAutosave's
 * last-write-wins abort cancels superseded requests in flight.
 */
export function saveProfile(
  body: PatchedCommunicationProfile,
  signal?: AbortSignal,
): Promise<CommunicationProfile> {
  return unwrap(
    api.PATCH("/api/v1/communications/profile/", { body, signal }),
  );
}

// --- issue choices -----------------------------------------------------------

export function useIssuesQuery() {
  return useQuery({
    queryKey: commKeys.issues(),
    queryFn: async () => {
      const data = await unwrap(api.GET("/api/v1/communications/issues/"));
      return data.results as IssueChoice[];
    },
  });
}

/**
 * A communicable issue — every issue with an action answered « Réalisée »
 * (marking it done in a plan sets that answer), plus any already chosen (see the
 * backend `issues/available/` endpoint). `has_plan_done` issues are
 * forced-featured (a Done plan action is a real achievement); the rest are
 * opt-in. Custom actions are neither counted nor shown.
 */
export interface AvailableIssue {
  issue_id: string;
  issue_name: string;
  thematic_id: string;
  thematic_name: string;
  css_class: string;
  icon: string;
  action_count: number;
  course_of_action_count: number;
  has_plan_done: boolean;
  is_selected: boolean;
  choice_id: string | null;
}

export const availableIssuesKey = () =>
  [...commKeys.issues(), "available"] as const;

/** The communicable issues to feature, grouped-able by thematic in the UI. */
export function useAvailableIssues() {
  return useQuery({
    queryKey: availableIssuesKey(),
    queryFn: async () =>
      (await unwrap(
        api.GET("/api/v1/communications/issues/available/"),
      )) as unknown as AvailableIssue[],
  });
}

/**
 * Tick or untick an issue in the cached list before the request answers.
 *
 * The checkbox reads from this query, so without it a click sat there until the
 * round trip and the refetch had both finished — long enough to feel broken.
 * `choice_id` is the server's to assign, so it stays null until the refetch;
 * the row that is mid-write is the only one the UI holds still.
 */
function optimisticIssue(
  qc: ReturnType<typeof useQueryClient>,
  match: (issue: AvailableIssue) => boolean,
  patch: Partial<AvailableIssue>,
) {
  const previous = qc.getQueryData<AvailableIssue[]>(availableIssuesKey());
  if (previous) {
    qc.setQueryData<AvailableIssue[]>(
      availableIssuesKey(),
      previous.map((issue) => (match(issue) ? { ...issue, ...patch } : issue)),
    );
  }
  return { previous };
}

function restoreIssues(
  qc: ReturnType<typeof useQueryClient>,
  context: { previous?: AvailableIssue[] } | undefined,
) {
  if (context?.previous) qc.setQueryData(availableIssuesKey(), context.previous);
}

/** Feature an issue on the showcase (creates an IssueToCommunicateChoice). */
export function useSelectIssue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (issueId: string) =>
      unwrap(
        api.POST("/api/v1/communications/issues/", {
          body: { issue: issueId } as never,
        }),
      ),
    onMutate: async (issueId) => {
      await qc.cancelQueries({ queryKey: availableIssuesKey() });
      return optimisticIssue(qc, (i) => i.issue_id === issueId, {
        is_selected: true,
      });
    },
    onError: (_err, _issueId, context) => restoreIssues(qc, context),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: availableIssuesKey() });
      void qc.invalidateQueries({ queryKey: commKeys.issues() });
    },
  });
}

export function useDeleteIssue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.DELETE("/api/v1/communications/issues/{id}/", {
          params: { path: { id } },
        }),
      ),
    onMutate: async (choiceId) => {
      await qc.cancelQueries({ queryKey: availableIssuesKey() });
      return optimisticIssue(qc, (i) => i.choice_id === choiceId, {
        is_selected: false,
        choice_id: null,
      });
    },
    onError: (_err, _choiceId, context) => restoreIssues(qc, context),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: commKeys.issues() });
      void qc.invalidateQueries({ queryKey: availableIssuesKey() });
    },
  });
}

// --- actions to communicate --------------------------------------------------

export function useActionsQuery() {
  return useQuery({
    queryKey: commKeys.actions(),
    queryFn: async () => {
      const data = await unwrap(api.GET("/api/v1/communications/actions/"));
      return data.results as ActionToCommunicate[];
    },
  });
}

/**
 * A carried-out action available to feature — a Carried-Out questionnaire action
 * or a Done action-plan action under one of the selected communicable issues
 * (backend `actions/available/`). `is_selected` reflects an existing
 * ActionToCommunicate (`communicate_id`); `custom_name` is its display label.
 */
export interface AvailableAction {
  action_id: string;
  action_name: string;
  goal_id: string;
  goal_name: string;
  issue_id: string;
  issue_name: string;
  issue_choice_id: string;
  thematic_id: string;
  thematic_name: string;
  css_class: string;
  comment: string;
  show_comment_publicly: boolean;
  is_selected: boolean;
  communicate_id: string | null;
  custom_name: string;
}

/**
 * The actions step reads and writes the member's own communication, or —
 * given the reviewed member's enterprise profile id (``memberId``) — the
 * communication a validator reviews, through the review routes.
 */
export const availableActionsKey = (memberId?: string) =>
  memberId
    ? ([...commKeys.actions(), "available", "review", memberId] as const)
    : ([...commKeys.actions(), "available"] as const);

export const customActionsKey = (memberId?: string) =>
  memberId
    ? ([...commKeys.customActions(), "review", memberId] as const)
    : commKeys.customActions();

/** After a write on review: the reviewed showcase renders it too. */
function invalidateReview(
  qc: ReturnType<typeof useQueryClient>,
  memberId?: string,
) {
  if (memberId) void qc.invalidateQueries({ queryKey: commKeys.review(memberId) });
}

const reviewPath = (memberId: string) => ({
  params: { path: { enterprise_profile_id: memberId } },
});

/** Carried-out actions available to feature, grouped-able by issue → goal. */
export function useAvailableActions(memberId?: string) {
  return useQuery({
    queryKey: availableActionsKey(memberId),
    queryFn: async () =>
      (await unwrap(
        memberId
          ? api.GET(
              "/api/v1/communications/review/{enterprise_profile_id}/actions/available/",
              reviewPath(memberId),
            )
          : api.GET("/api/v1/communications/actions/available/"),
      )) as unknown as AvailableAction[],
  });
}

/** Feature a carried-out action (creates an ActionToCommunicate under its issue). */
export function useSelectAction(memberId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { actionId: string; issueChoiceId: string }) => {
      const body = {
        action: vars.actionId,
        issue_to_communicate_choice: vars.issueChoiceId,
      } as never;
      return unwrap(
        memberId
          ? api.POST(
              "/api/v1/communications/review/{enterprise_profile_id}/actions/",
              { ...reviewPath(memberId), body },
            )
          : api.POST("/api/v1/communications/actions/", { body }),
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: availableActionsKey(memberId) });
      void qc.invalidateQueries({ queryKey: commKeys.actions() });
      invalidateReview(qc, memberId);
    },
  });
}

export function saveActionName(
  id: string,
  customName: string,
  signal?: AbortSignal,
  memberId?: string,
): Promise<ActionToCommunicate> {
  const body = { custom_name: customName };
  return unwrap(
    memberId
      ? api.PATCH(
          "/api/v1/communications/review/{enterprise_profile_id}/actions/{id}/",
          {
            params: { path: { enterprise_profile_id: memberId, id } },
            body: body as never,
            signal,
          },
        )
      : api.PATCH("/api/v1/communications/actions/{id}/", {
          params: { path: { id } },
          body,
          signal,
        }),
  ) as Promise<ActionToCommunicate>;
}

export function useDeleteAction(memberId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(
        memberId
          ? api.DELETE(
              "/api/v1/communications/review/{enterprise_profile_id}/actions/{id}/",
              { params: { path: { enterprise_profile_id: memberId, id } } },
            )
          : api.DELETE("/api/v1/communications/actions/{id}/", {
              params: { path: { id } },
            }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: commKeys.actions() });
      void qc.invalidateQueries({ queryKey: availableActionsKey(memberId) });
      invalidateReview(qc, memberId);
    },
  });
}

// --- custom actions to communicate -------------------------------------------

export function useCustomActionsQuery(memberId?: string) {
  return useQuery({
    queryKey: customActionsKey(memberId),
    queryFn: async () => {
      const data = await unwrap(
        memberId
          ? api.GET(
              "/api/v1/communications/review/{enterprise_profile_id}/custom-actions/",
              reviewPath(memberId),
            )
          : api.GET("/api/v1/communications/custom-actions/"),
      );
      return data.results as CustomActionToCommunicate[];
    },
  });
}

export function saveCustomAction(
  id: string,
  body: Partial<CustomActionToCommunicate>,
  signal?: AbortSignal,
  memberId?: string,
): Promise<CustomActionToCommunicate> {
  return unwrap(
    memberId
      ? api.PATCH(
          "/api/v1/communications/review/{enterprise_profile_id}/custom-actions/{id}/",
          {
            params: { path: { enterprise_profile_id: memberId, id } },
            body: body as never,
            signal,
          },
        )
      : api.PATCH("/api/v1/communications/custom-actions/{id}/", {
          params: { path: { id } },
          body: body as never,
          signal,
        }),
  ) as Promise<CustomActionToCommunicate>;
}

export function useCreateCustomAction(memberId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { goal: string; description: string }) => {
      const body = { ...vars, selected: true } as CustomActionToCommunicate;
      return unwrap(
        memberId
          ? api.POST(
              "/api/v1/communications/review/{enterprise_profile_id}/custom-actions/",
              { ...reviewPath(memberId), body: body as never },
            )
          : api.POST("/api/v1/communications/custom-actions/", { body }),
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: customActionsKey(memberId) });
      invalidateReview(qc, memberId);
    },
  });
}

export function useDeleteCustomAction(memberId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(
        memberId
          ? api.DELETE(
              "/api/v1/communications/review/{enterprise_profile_id}/custom-actions/{id}/",
              { params: { path: { enterprise_profile_id: memberId, id } } },
            )
          : api.DELETE("/api/v1/communications/custom-actions/{id}/", {
              params: { path: { id } },
            }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: customActionsKey(memberId) });
      invalidateReview(qc, memberId);
    },
  });
}

// --- goals (custom goal create / rename / delete, E-6) -----------------------

/** A goal to render in the actions step (system-with-actions or custom). */
export interface AvailableGoal {
  goal_id: string;
  issue_id: string;
  issue_name: string;
  css_class: string;
  name: string;
  is_custom: boolean;
}

/**
 * The objectives of the member's own communication, or — given the reviewed
 * member's enterprise profile id — of the communication a validator reviews.
 */
export const availableGoalsKey = (memberId?: string) =>
  memberId
    ? ([...commKeys.all, "goals", "available", "review", memberId] as const)
    : ([...commKeys.all, "goals", "available"] as const);

/** After an objective write: its list, and what the write shows elsewhere. */
function invalidateGoals(
  qc: ReturnType<typeof useQueryClient>,
  memberId?: string,
) {
  void qc.invalidateQueries({ queryKey: availableGoalsKey(memberId) });
  // A new objective's first action is a new custom action too.
  void qc.invalidateQueries({ queryKey: customActionsKey(memberId) });
  // The reviewed showcase renders the objectives.
  invalidateReview(qc, memberId);
}

export function useAvailableGoals(memberId?: string) {
  return useQuery({
    queryKey: availableGoalsKey(memberId),
    queryFn: async () => {
      // The `available` action returns a hand-built payload drf-spectacular
      // can't type from the viewset serializer, so cast to the known shape.
      const data = await unwrap(
        memberId
          ? api.GET(
              "/api/v1/communications/review/{enterprise_profile_id}/goals/available/",
              { params: { path: { enterprise_profile_id: memberId } } },
            )
          : api.GET("/api/v1/communications/goals/available/"),
      );
      return data as unknown as AvailableGoal[];
    },
  });
}

export function useCreateGoal(memberId?: string) {
  const qc = useQueryClient();
  return useMutation({
    // A custom goal is created with its first action (an objective always
    // keeps at least one), as the Django modal creates it.
    mutationFn: (body: { issue: string; name: string; first_action: string }) =>
      unwrap(
        memberId
          ? api.POST(
              "/api/v1/communications/review/{enterprise_profile_id}/goals/",
              {
                params: { path: { enterprise_profile_id: memberId } },
                body: body as never,
              },
            )
          : api.POST("/api/v1/communications/goals/", { body: body as never }),
      ),
    onSuccess: () => invalidateGoals(qc, memberId),
  });
}

export function useDeleteGoal(memberId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(
        memberId
          ? api.DELETE(
              "/api/v1/communications/review/{enterprise_profile_id}/goals/{id}/",
              { params: { path: { enterprise_profile_id: memberId, id } } },
            )
          : api.DELETE("/api/v1/communications/goals/{id}/", {
              params: { path: { id } },
            }),
      ),
    onSuccess: () => invalidateGoals(qc, memberId),
  });
}

export function useRenameGoal(memberId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; customName: string }) =>
      unwrap(
        memberId
          ? api.POST(
              "/api/v1/communications/review/{enterprise_profile_id}/goals/{id}/customize/",
              {
                params: { path: { enterprise_profile_id: memberId, id: vars.id } },
                body: { custom_name: vars.customName } as never,
              },
            )
          : api.POST("/api/v1/communications/goals/{id}/customize/", {
              params: { path: { id: vars.id } },
              body: { custom_name: vars.customName } as never,
            }),
      ),
    onSuccess: () => invalidateGoals(qc, memberId),
  });
}

// --- preview + approval ------------------------------------------------------

/**
 * Fetch the live Django-rendered showcase HTML (JWT-authenticated), so the SPA
 * can embed the real public page in an iframe. Returns HTML text, not JSON, so
 * it bypasses the typed client (bearer token + refresh-on-401, like the export
 * downloads). Errors carry the HTTP status for 403/404 messaging.
 *
 * Bypassing the typed client also bypasses its `Accept-Language` middleware,
 * so this document — unlike every other response the SPA renders — used to
 * arrive in whatever language the *browser* asked for, not the one the user
 * picked in the app. Send the header here too: the whole point of this iframe
 * is that one Django template serves both apps, and it cannot serve them in
 * two different languages.
 */
async function fetchPageHtml(path: string): Promise<string> {
  const url = `${API_ORIGIN}${path}`;
  const request = (token: string | null) => {
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    if (i18n.language) headers["Accept-Language"] = i18n.language;
    return fetch(url, { headers });
  };
  let response = await request(getAccessToken());
  if (response.status === 401) {
    const refreshed = await refreshAccessToken();
    if (refreshed) response = await request(refreshed);
  }
  if (!response.ok) {
    const error = new Error(`Preview failed (${response.status})`) as Error & {
      status: number;
    };
    error.status = response.status;
    throw error;
  }
  return response.text();
}

export function usePreviewHtml(enabled = true) {
  return useQuery({
    // Language is part of the key: the server renders this document per
    // Accept-Language, so a language switch must refetch rather than serve a
    // stale-locale cache. Asking for the right language is only half of it —
    // without this the switch re-renders and the iframe keeps the document it
    // already had, watermark and all.
    queryKey: [...commKeys.preview(), "html", i18n.language],
    enabled,
    queryFn: () => fetchPageHtml("/api/v1/communications/preview/html/"),
  });
}

export function useReviewHtml(enterpriseProfileId: string) {
  return useQuery({
    queryKey: [...commKeys.review(enterpriseProfileId), "html", i18n.language],
    enabled: Boolean(enterpriseProfileId),
    retry: false,
    queryFn: () =>
      fetchPageHtml(
        `/api/v1/communications/review/${enterpriseProfileId}/html/`,
      ),
  });
}

export function useRequestApproval() {
  return useMutation({
    mutationFn: () =>
      unwrap(api.POST("/api/v1/communications/approval-request/", {})),
  });
}

// --- partner review ----------------------------------------------------------

export type PendingReview =
  components["schemas"]["PendingReview"] & { status: "new" | "resubmitted" };

/** Communications awaiting validation — the validator's Révision list. */
export function usePendingReviews() {
  return useQuery({
    queryKey: commKeys.pendingReviews(),
    queryFn: async () => {
      const data = await unwrap(
        api.GET("/api/v1/communications/pending-reviews/"),
      );
      return (data.results ?? []) as PendingReview[];
    },
  });
}

export function usePublish() {
  const qc = useQueryClient();
  return useMutation({
    // The view returns the created CommunicationWebPage, but drf-spectacular
    // declares the response as empty, so cast the (untyped) result.
    mutationFn: (enterpriseProfileId: string) =>
      unwrap(
        api.POST("/api/v1/communications/publish/{enterprise_profile_id}/", {
          params: { path: { enterprise_profile_id: enterpriseProfileId } },
        }),
      ) as unknown as Promise<CommunicationWebPage>,
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: commKeys.review(id) });
      // The published item drops off the pending-validations list.
      qc.invalidateQueries({ queryKey: commKeys.pendingReviews() });
    },
  });
}
